import {
  IMarketingInsight,
  marketingInsightPart,
} from './marketingInsightsModel';
import {
  formatMarketingCount,
  formatMarketingPct,
  formatMarketingRate,
  isMarketingChannel,
  marketingChannelFromKey,
  marketingDeviceKey,
} from './marketingModel';
import {
  IMarketingCount,
  IMarketingDeviceRow,
  IMarketingFilter,
  IMarketingPageMetrics,
  IMarketingPageRow,
  IMarketingVisitorBucket,
  IMarketingVisitorKpis,
  IMarketingVisitorMatrixCell,
  IMarketingVisitorSample,
} from './types';

/**
 * ТЗ 0103 — «Путь клиента» (design-erp §3.5): посетители своего счётчика и
 * конверсия страниц сайта. Чистая часть: корзины, группы и типы страниц,
 * знаменатель конверсии, малая база, точки «трафик × конверсия», матрица
 * «первый канал × канал заявки». Без React, Apollo и i18n — подписи ключами
 * `marketing.visitors.*` / `marketing.pages.*`.
 *
 * Главное правило раздела — пропуск ≠ ноль: `null` из контракта (GA4 с
 * фильтром каналов, GSC не собран, свой счётчик ещё не собирал) не
 * превращается в 0.
 */

/* ── Фильтр вкладки ──────────────────────────────────────────────────── */

/**
 * Фильтр «Пути клиента»: период, каналы, курсы, юрлицо. Кампании,
 * объявления, предложения и модель атрибуции у посетителей и страниц не
 * действуют (лиды окна — без модели, страницы — не реклама): в запрос не
 * уходят, экран говорит об этом подсказкой.
 */
export const marketingJourneyFilter = (
  filter: IMarketingFilter,
): IMarketingFilter => ({
  dateFrom: filter.dateFrom,
  dateTo: filter.dateTo,
  channels: filter.channels?.length ? filter.channels : null,
  familySlugs: filter.familySlugs?.length ? filter.familySlugs : null,
  legalEntityId: filter.legalEntityId || null,
});

/** Есть ли в фильтре то, что «Путь клиента» не учитывает. */
export const marketingJourneyIgnoresFilter = (
  filter: IMarketingFilter,
  defaultModel: string,
): boolean =>
  Boolean(filter.offers?.length) ||
  Boolean(filter.campaignIds?.length) ||
  Boolean(filter.adsetIds?.length) ||
  Boolean(filter.adIds?.length) ||
  (Boolean(filter.model) && filter.model !== defaultModel);

/* ── Корзины ─────────────────────────────────────────────────────────── */

/** «Сколько визитов до заявки» — ключи сервера по порядку. */
export const MARKETING_VISIT_BUCKETS = [
  '1',
  '2',
  '3_4',
  '5_9',
  '10_plus',
] as const;

/** «Сколько времени до заявки»: в тот же визит, < 1 дня, 1–3, … 31+. */
export const MARKETING_TIME_TO_LEAD_BUCKETS = [
  'same_visit',
  'lt1',
  '1_3',
  '4_7',
  '8_30',
  '31_plus',
] as const;

/** «Сколько страниц до заявки». */
export const MARKETING_PAGES_BEFORE_BUCKETS = [
  '0_1',
  '2',
  '3_4',
  '5_9',
  '10_plus',
] as const;

/**
 * Корзины в порядке словаря; незнакомый ключ сервера — в хвосте (виден, а
 * не пропал), недостающий — нулём не дорисовывается: корзин ровно столько,
 * сколько прислал сервер.
 */
export const marketingOrderVisitorBuckets = (
  rows: ReadonlyArray<IMarketingVisitorBucket>,
  keys: ReadonlyArray<string>,
): IMarketingVisitorBucket[] => {
  const rank = (key: string) => {
    const at = keys.indexOf(key);
    return at < 0 ? keys.length : at;
  };
  return [...rows].sort((a, b) => rank(a.key) - rank(b.key));
};

/** Ключ подписи корзины или `null` — незнакомая, подписать кодом. */
export const marketingVisitorBucketKey = (
  group: 'visits' | 'days' | 'pages',
  key: string,
): string | null => {
  const known: Record<typeof group, ReadonlyArray<string>> = {
    visits: MARKETING_VISIT_BUCKETS,
    days: MARKETING_TIME_TO_LEAD_BUCKETS,
    pages: MARKETING_PAGES_BEFORE_BUCKETS,
  };
  return known[group].includes(key)
    ? `marketing.visitors.buckets.${group}.${key}`
    : null;
};

/* ── Длительность «до заявки» ────────────────────────────────────────── */

export type MarketingDurationUnit = 'minutes' | 'hours' | 'days' | 'sameDay';

export interface IMarketingDuration {
  unit: MarketingDurationUnit;
  value: number;
}

/**
 * Дни сервера (`0.016`) → понятная единица: меньше часа — минуты, меньше
 * суток — часы, иначе дни с десятыми. `null` — медианы нет.
 *
 * `rounded` — время лида округлено до дня (`roundedToDay`, все роли, кроме
 * Admin): дни целые, ноль — «в тот же день», а не «1 мин».
 */
export const marketingDuration = (
  days: number | null | undefined,
  rounded = false,
): IMarketingDuration | null => {
  if (days === null || days === undefined || !Number.isFinite(days)) {
    return null;
  }
  if (rounded) {
    const whole = Math.max(0, Math.round(days));
    return whole === 0
      ? { unit: 'sameDay', value: 0 }
      : { unit: 'days', value: whole };
  }
  const hours = days * 24;
  if (hours < 1) {
    return { unit: 'minutes', value: Math.max(1, Math.round(hours * 60)) };
  }
  if (days < 1) return { unit: 'hours', value: Math.round(hours * 10) / 10 };
  return { unit: 'days', value: Math.round(days * 10) / 10 };
};

/* ── Свой счётчик: отброшенные маячки ───────────────────────────────── */

/**
 * Причины отброса маячка (`siteTracking.dropped`): чужой сайт, бот, лимит
 * частоты, размер, очередь, формат, сбой приёма; ревью 0103 S1 — без
 * подписи (`MARKETING_TRACK_REQUIRE_SIGNED`) и лимит новых посетителей.
 */
export const MARKETING_TRACK_DROP_REASONS = [
  'dropped_origin',
  'dropped_bot',
  'dropped_rate',
  'dropped_visitors',
  'dropped_unsigned',
  'dropped_size',
  'dropped_queue',
  'dropped_invalid',
  'dropped_error',
] as const;

export type MarketingTrackDropReason = typeof MARKETING_TRACK_DROP_REASONS[number];

export const isMarketingTrackDropReason = (
  value: string | null | undefined,
): value is MarketingTrackDropReason =>
  Boolean(value) &&
  (MARKETING_TRACK_DROP_REASONS as readonly string[]).includes(value as string);

/**
 * Отброшенные по причине → строки подписи: только ненулевые, по убыванию
 * числа; незнакомая причина — кодом (новое с сервера видно).
 */
export const marketingTrackDrops = (
  dropped: ReadonlyArray<{ value: string; count: number }> | null | undefined,
): Array<{ code: string; labelKey: string | null; count: number }> =>
  (dropped ?? [])
    .filter((item) => item.count > 0)
    .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value))
    .map((item) => ({
      code: item.value,
      labelKey: isMarketingTrackDropReason(item.value)
        ? `marketing.quality.trackingDrops.${item.value}`
        : null,
      count: item.count,
    }));

/* ── Анонимный посетитель ────────────────────────────────────────────── */

/**
 * Id посетителя (`marketingVisitors.rows.id`, ревью 0103 S5) — 16
 * шестнадцатеричных знаков, не порядковый номер: по нему нельзя перебрать
 * соседей. Кривой — сервер отвечает `bad_filter`, поэтому адрес с ним панель
 * не открывает.
 */
export const MARKETING_VISITOR_ID_RE = /^[0-9a-f]{16}$/;

export const isMarketingVisitorId = (
  value: string | null | undefined,
): value is string => MARKETING_VISITOR_ID_RE.test(value ?? '');

/** Знаков id в подписи («Посетитель 288ccd6c…»); целиком — в подсказке. */
export const MARKETING_VISITOR_ID_SHOWN = 8;

export const marketingVisitorShortId = (id: string): string =>
  id.length > MARKETING_VISITOR_ID_SHOWN
    ? `${id.slice(0, MARKETING_VISITOR_ID_SHOWN)}…`
    : id;

/* ── Каналы пути ─────────────────────────────────────────────────────── */

/** Канал заявки `none`: лид не из визита (звонок, другое устройство). */
export const MARKETING_JOURNEY_NO_VISIT = 'none';

/**
 * Канал визита кодом базы (`seo_google`) → код enum (`SEO_GOOGLE`) для
 * подписи и цвета; незнакомый — как пришёл, `none` — `none`.
 */
export const marketingJourneyChannel = (
  code: string | null | undefined,
): string => {
  if (!code) return MARKETING_JOURNEY_NO_VISIT;
  if (code === MARKETING_JOURNEY_NO_VISIT) return code;
  return marketingChannelFromKey(code);
};

/** Ключ подписи канала пути; `null` — незнакомый код (подписать кодом). */
export const marketingJourneyChannelKey = (
  code: string | null | undefined,
): string | null => {
  const channel = marketingJourneyChannel(code);
  if (channel === MARKETING_JOURNEY_NO_VISIT) {
    return 'marketing.visitors.noVisitChannel';
  }
  return isMarketingChannel(channel) ? `marketing.channels.${channel}` : null;
};

export interface IMarketingJourneyMatrix {
  rows: string[];
  cols: string[];
  cells: Record<string, number>;
}

/** Ключ ячейки матрицы «первый канал × канал заявки». */
export const marketingJourneyCellKey = (first: string, lead: string): string =>
  `${first}|${lead}`;

/**
 * Матрица каналов: строки — первый канал визита, колонки — канал визита с
 * заявкой (оба кодом enum), по убыванию итога; `none` (заявка не из
 * визита) — последней колонкой.
 */
export const marketingJourneyMatrix = (
  cells: ReadonlyArray<IMarketingVisitorMatrixCell>,
): IMarketingJourneyMatrix => {
  const rowTotals = new Map<string, number>();
  const colTotals = new Map<string, number>();
  const values: Record<string, number> = {};
  cells.forEach((cell) => {
    const first = marketingJourneyChannel(cell.firstChannel);
    const lead = marketingJourneyChannel(cell.leadChannel);
    rowTotals.set(first, (rowTotals.get(first) ?? 0) + cell.leads);
    colTotals.set(lead, (colTotals.get(lead) ?? 0) + cell.leads);
    const key = marketingJourneyCellKey(first, lead);
    values[key] = (values[key] ?? 0) + cell.leads;
  });
  const order = (totals: Map<string, number>) =>
    Array.from(totals.entries())
      .sort(
        (a, b) =>
          Number(a[0] === MARKETING_JOURNEY_NO_VISIT) -
            Number(b[0] === MARKETING_JOURNEY_NO_VISIT) ||
          b[1] - a[1] ||
          a[0].localeCompare(b[0]),
      )
      .map(([key]) => key);
  return { rows: order(rowTotals), cols: order(colTotals), cells: values };
};

/** Ячейка матрицы, по которой отобраны примеры путей. */
export interface IMarketingJourneyCell {
  first: string;
  lead: string;
}

/** Пример пути попадает в ячейку (каналы — кодом enum). */
export const marketingSampleInCell = (
  row: IMarketingVisitorSample,
  cell: IMarketingJourneyCell | null,
): boolean =>
  !cell ||
  (marketingJourneyChannel(row.firstChannel) === cell.first &&
    marketingJourneyChannel(row.leadChannel) === cell.lead);

/** Примеров путей на страницу таблицы. */
export const MARKETING_VISITOR_SAMPLES_PAGE = 25;

/**
 * Примеров, среди которых ищутся пути ячейки матрицы: отбора по первому
 * каналу у сервера нет — берём последние 200 заявок окна (потолок сервера)
 * и отбираем на экране; экран так и подписывает.
 */
export const MARKETING_VISITOR_CELL_SCAN = 200;

/** Исходы примера пути — по силе: продажа сильнее подписанного договора. */
export const MARKETING_VISITOR_OUTCOMES = [
  'sale',
  'contract_signed',
  'contract_sent',
  'lead',
  'none',
] as const;

export type MarketingVisitorOutcome = typeof MARKETING_VISITOR_OUTCOMES[number];

export const isMarketingVisitorOutcome = (
  value: string | null | undefined,
): value is MarketingVisitorOutcome =>
  Boolean(value) &&
  (MARKETING_VISITOR_OUTCOMES as readonly string[]).includes(value as string);

/** Тон исхода: продажа и подпись — хорошо, отправлен — справочно. */
export const marketingVisitorOutcomeTone = (
  outcome: string,
): 'ok' | 'info' | 'neutral' => {
  if (outcome === 'sale' || outcome === 'contract_signed') return 'ok';
  if (outcome === 'contract_sent') return 'info';
  return 'neutral';
};

/** Шаги таймлайна посетителя. */
export const MARKETING_VISITOR_EVENT_KINDS = [
  'visit',
  'lead',
  'contract_sent',
  'contract_signed',
  'sale',
] as const;

export type MarketingVisitorEventKind = typeof MARKETING_VISITOR_EVENT_KINDS[number];

export const isMarketingVisitorEventKind = (
  value: string | null | undefined,
): value is MarketingVisitorEventKind =>
  Boolean(value) &&
  (MARKETING_VISITOR_EVENT_KINDS as readonly string[]).includes(
    value as string,
  );

/* ── Типы и группы страниц ───────────────────────────────────────────── */

/**
 * Типы страниц по умолчанию (`constants.DEFAULT_PAGE_TYPES` сервера) + `other`
 * (ни одно правило не совпало). Свои типы из «Настроек» подписываются кодом.
 */
export const MARKETING_PAGE_TYPES = [
  'b2b',
  'dofin',
  'course',
  'lead_magnet',
  'catalog',
  'pricing',
  'blog',
  'tools',
  'about',
  'thanks',
  'home',
  'other',
] as const;

export type MarketingPageType = typeof MARKETING_PAGE_TYPES[number];

/** Тип не определён (фильтр сервера `none`). */
export const MARKETING_PAGE_TYPE_NONE = 'none';

/**
 * Строка «прочих страниц» своего счётчика (ревью 0103 S2): пути вне правил
 * типов и сверх суточного потолка новых страниц сложены в одну строку. Это
 * не адрес сайта — подпись словами, ссылки на сайт нет.
 */
export const MARKETING_OTHER_PAGE_PATH = '/__other__';

export const marketingIsOtherPage = (
  path: string | null | undefined,
): boolean => path === MARKETING_OTHER_PAGE_PATH;

export const isMarketingPageType = (
  value: string | null | undefined,
): value is MarketingPageType =>
  Boolean(value) &&
  (MARKETING_PAGE_TYPES as readonly string[]).includes(value as string);

/** Ключ подписи типа страницы; свой тип — `null` (подписать кодом). */
export const marketingPageTypeKey = (
  value: string | null | undefined,
): string | null => {
  if (!value || value === MARKETING_PAGE_TYPE_NONE) {
    return 'marketing.pages.typeNone';
  }
  return isMarketingPageType(value) ? `marketing.pages.types.${value}` : null;
};

/**
 * Группы страниц — чипы «Страниц» (`pg` адреса): курсы (с каталогом и
 * рассрочкой), Dofinansowanie, для компаний, блог и материалы, главная и
 * прочие. Не больше пяти групп + «Все»: шесть чипов читаются одним рядом.
 */
export const MARKETING_PAGE_GROUPS = [
  'course',
  'dofin',
  'b2b',
  'blog',
  'home',
] as const;

export type MarketingPageGroup = typeof MARKETING_PAGE_GROUPS[number];

export const isMarketingPageGroup = (
  value: string | null | undefined,
): value is MarketingPageGroup =>
  Boolean(value) &&
  (MARKETING_PAGE_GROUPS as readonly string[]).includes(value as string);

/** Типы, которые группа берёт явно. «Главная и прочие» — ещё и все остальные. */
export const MARKETING_PAGE_GROUP_TYPES: Readonly<
  Record<MarketingPageGroup, ReadonlyArray<string>>
> = {
  course: ['course', 'catalog', 'pricing'],
  dofin: ['dofin'],
  b2b: ['b2b'],
  blog: ['blog', 'lead_magnet', 'tools'],
  home: ['home', 'about', 'thanks', 'other', MARKETING_PAGE_TYPE_NONE],
};

/** Группа, в которую попадает тип: свой тип из «Настроек» — «прочие». */
export const marketingPageGroupOf = (
  type: string | null | undefined,
): MarketingPageGroup => {
  const code = type || MARKETING_PAGE_TYPE_NONE;
  const found = MARKETING_PAGE_GROUPS.find((group) =>
    MARKETING_PAGE_GROUP_TYPES[group].includes(code),
  );
  return found ?? 'home';
};

/**
 * Типы запроса для группы (`pageTypes`, ИЛИ). «Главная и прочие» — ещё и
 * свои типы из «Настроек» (`known` — коды типов справочника), иначе такие
 * страницы не нашлись бы ни в одной группе.
 */
export const marketingPageGroupTypes = (
  group: MarketingPageGroup | null,
  known: ReadonlyArray<string> = [],
): string[] | null => {
  if (!group) return null;
  const own = [...MARKETING_PAGE_GROUP_TYPES[group]];
  if (group !== 'home') return own;
  const extra = known.filter(
    (type) => marketingPageGroupOf(type) === 'home' && !own.includes(type),
  );
  return [...own, ...extra];
};

/** Числа чипов групп из `typeCounts` (строки до фильтра типов). */
export const marketingPageGroupCounts = (
  typeCounts: ReadonlyArray<IMarketingCount>,
): Record<MarketingPageGroup | 'all', number> => {
  const out = {
    all: 0,
    course: 0,
    dofin: 0,
    b2b: 0,
    blog: 0,
    home: 0,
  } as Record<MarketingPageGroup | 'all', number>;
  typeCounts.forEach((item) => {
    if (item.value === 'all') {
      out.all = item.count;
      return;
    }
    out[marketingPageGroupOf(item.value)] += item.count;
  });
  return out;
};

/* ── Знаменатель и малая база ────────────────────────────────────────── */

/** Чем делится конверсия страницы: свой счётчик или сессии GA4. */
export const MARKETING_PAGE_DENOMINATORS = ['own', 'ga4'] as const;

export type MarketingPageDenominator = typeof MARKETING_PAGE_DENOMINATORS[number];

export const isMarketingPageDenominator = (
  value: string | null | undefined,
): value is MarketingPageDenominator =>
  Boolean(value) &&
  (MARKETING_PAGE_DENOMINATORS as readonly string[]).includes(value as string);

/**
 * Меньше 100 посетителей за окно — «мало данных»: конверсия 1 из 20 — не
 * вывод, а шум (серым, без тона).
 */
export const MARKETING_PAGES_MIN_TRAFFIC = 100;

export interface IMarketingPageTraffic {
  /** Входы своего счётчика или сессии GA4; `null` — не собрано. */
  value: number | null;
  kind: MarketingPageDenominator | null;
}

/**
 * «Посетители» страницы — знаменатель её конверсии: входы своего счётчика
 * (`own`) или сессии GA4 (`ga4`); знаменателя нет — по тому, что собрано.
 */
export const marketingPageTraffic = (
  metrics: IMarketingPageMetrics | null | undefined,
): IMarketingPageTraffic => {
  if (!metrics) return { value: null, kind: null };
  const own = metrics.fpEntrances ?? null;
  const ga4 = metrics.ga4Sessions ?? null;
  if (metrics.denominator === 'own') return { value: own, kind: 'own' };
  if (metrics.denominator === 'ga4') return { value: ga4, kind: 'ga4' };
  if (ga4 !== null) return { value: ga4, kind: 'ga4' };
  if (own !== null) return { value: own, kind: 'own' };
  return { value: null, kind: null };
};

/** Мало посетителей для вывода о конверсии (или трафик не собран). */
export const marketingPageSmallBase = (
  metrics: IMarketingPageMetrics | null | undefined,
): boolean => {
  const { value } = marketingPageTraffic(metrics);
  return value === null || value < MARKETING_PAGES_MIN_TRAFFIC;
};

/** Показатель «посетителей» для сортировки сервера по знаменателю окна. */
export const marketingPageTrafficKey = (
  denominator: string | null | undefined,
): 'fpEntrances' | 'ga4Sessions' =>
  denominator === 'own' ? 'fpEntrances' : 'ga4Sessions';

/** Сортируемые столбцы таблицы страниц (`traffic` — по знаменателю окна). */
export const MARKETING_PAGE_SORT_KEYS = [
  'path',
  'traffic',
  'crmLeadsLanding',
  'crmLeadsAssisted',
  'landingConversion',
  'salesLanding',
  'gscClicks',
  'ga4Sessions',
] as const;

export type MarketingPageSortKey = typeof MARKETING_PAGE_SORT_KEYS[number];

/** Столбцы, у которых есть сортировка «по изменению». */
export const MARKETING_PAGE_DELTA_SORT_KEYS: ReadonlyArray<MarketingPageSortKey> = [
  'traffic',
  'crmLeadsLanding',
  'crmLeadsAssisted',
  'landingConversion',
  'salesLanding',
  'gscClicks',
  'ga4Sessions',
];

/**
 * `orderBy` сервера страниц: «посетители» — показатель знаменателя окна,
 * «по изменению» — `delta_<показатель>` (только со сравнением). `null` —
 * порядок сервера (по лидам посадочной).
 */
export const marketingPagesOrderBy = (
  sort: { key: MarketingPageSortKey; desc: boolean } | null,
  {
    denominator,
    byChange,
  }: { denominator: string | null | undefined; byChange: boolean },
): string | null => {
  if (!sort) return null;
  const key =
    sort.key === 'traffic' ? marketingPageTrafficKey(denominator) : sort.key;
  const delta =
    byChange && MARKETING_PAGE_DELTA_SORT_KEYS.includes(sort.key)
      ? `delta_${key}`
      : key;
  return `${sort.desc ? '-' : ''}${delta}`;
};

/* ── «Конверсия страниц» и «Трафик × конверсия» ──────────────────────── */

/** Страниц в «Конверсии страниц» (по лидам посадочной). */
export const MARKETING_PAGES_RANK_LIMIT = 15;

/**
 * Страниц в «Где теряем заявки» и на точках «Трафик × конверсия» (по
 * посетителям). Финальная проверка 0103 (F-E12): медиана — по всем
 * страницам с базой, а не по первым 60, поэтому — предел сервера (200):
 * страниц с ≥ 100 посетителями за окно меньше.
 */
export const MARKETING_PAGES_BUBBLE_LIMIT = 200;

/**
 * Ответ «по посетителям» обрезан пределом, а последняя строка ещё с базой:
 * страницы с базой могли остаться за пределом — медиана тогда по первым N
 * (подпись под списком). `false` — пришли все страницы с базой.
 */
export const marketingPageLossesTruncated = (
  rows: ReadonlyArray<IMarketingPageRow>,
  totalCount: number,
): boolean => {
  if (totalCount <= rows.length || !rows.length) return false;
  const { value } = marketingPageTraffic(rows[rows.length - 1].metrics);
  return value !== null && value >= MARKETING_PAGES_MIN_TRAFFIC;
};

/** Строк таблицы страниц за страницу. */
export const MARKETING_PAGES_TABLE_PAGE = 25;

export interface IMarketingPageBubblePoint {
  id: string;
  /** Посетители (знаменатель). */
  x: number;
  /** Конверсия посадочной, доля. */
  y: number;
  /** Лиды посадочной (радиус). */
  r: number;
  /** Много трафика, слабая конверсия — «улучшить». */
  improve: boolean;
}

export interface IMarketingPageBubbles {
  points: IMarketingPageBubblePoint[];
  /** Медианы осей — границы квадранта; `null` — точек нет. */
  medianX: number | null;
  medianY: number | null;
  /** Страниц без трафика или конверсии — не нарисованы. */
  skipped: number;
}

const median = (values: number[]): number | null => {
  const sorted = values.filter(Number.isFinite).sort((a, b) => a - b);
  if (!sorted.length) return null;
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

/**
 * Точки «трафик × конверсия»: x — посетители, y — конверсия посадочной,
 * радиус — лиды. Страницы с малой базой (< 100) и без конверсии не
 * рисуются (`skipped`): точка «1 лид из 3» уехала бы в потолок графика.
 * Квадрант «улучшить» — трафик не ниже медианы, конверсия не выше медианы.
 */
export const marketingPageBubbles = (
  rows: ReadonlyArray<IMarketingPageRow>,
): IMarketingPageBubbles => {
  const drawable = rows
    .map((row) => {
      const { value } = marketingPageTraffic(row.metrics);
      const conversion = row.metrics.landingConversion ?? null;
      return value !== null &&
        value >= MARKETING_PAGES_MIN_TRAFFIC &&
        conversion !== null
        ? {
            id: row.path,
            x: value,
            y: conversion,
            r: row.metrics.crmLeadsLanding,
          }
        : null;
    })
    .filter(
      (point): point is { id: string; x: number; y: number; r: number } =>
        point !== null,
    );
  const medianX = median(drawable.map((point) => point.x));
  const medianY = median(drawable.map((point) => point.y));
  return {
    points: drawable.map((point) => ({
      ...point,
      improve:
        medianX !== null &&
        medianY !== null &&
        drawable.length >= 4 &&
        point.x >= medianX &&
        point.y <= medianY,
    })),
    medianX,
    medianY,
    skipped: rows.length - drawable.length,
  };
};

/* ── «Где теряем заявки» (ТЗ 0103, пакет 2) ──────────────────────────── */

/**
 * Страниц с базой — не меньше: медиана двух-трёх страниц — не ориентир,
 * а одна из них (то же правило, что у квадранта «улучшить» пузырьков).
 */
export const MARKETING_PAGES_LOSS_MIN_PAGES = 4;

/**
 * Состояние списка: посетителей за окно не считали (конверсий нет) / мало
 * страниц с базой / медиана 0 % (у большинства страниц заявок нет —
 * сравнивать не с чем) / посчитано.
 */
export type MarketingPageLossState =
  | 'noDenominator'
  | 'smallBase'
  | 'zeroMedian'
  | 'ok';

export interface IMarketingPageLoss {
  path: string;
  pageType?: string | null;
  /** Посетители (знаменатель конверсии окна). */
  traffic: number;
  /** Конверсия посадочной, доля. */
  conversion: number;
  /** Лиды посадочной. */
  leads: number;
  /** Недополучено заявок: посетители × (медиана − конверсия), > 0. */
  lost: number;
}

export interface IMarketingPageLosses {
  state: MarketingPageLossState;
  /** Страниц с базой (≥ `MARKETING_PAGES_MIN_TRAFFIC`) и известной конверсией. */
  based: number;
  /** Медианная конверсия этих страниц; `null` — страниц мало. */
  median: number | null;
  /** Страницы ниже медианы — по недополученным заявкам, больше — выше. */
  rows: IMarketingPageLoss[];
  /** Сумма недополученных по всем строкам. */
  lostTotal: number;
}

/**
 * Недополучено заявок страницей: сколько пришло бы при медианной
 * конверсии, минус сколько пришло. Страница не ниже медианы — 0.
 */
export const marketingPageLostLeads = (
  traffic: number,
  conversion: number,
  medianConversion: number,
): number => Math.max(0, traffic * (medianConversion - conversion));

/**
 * «Где теряем заявки» (пакет 2, вместо пузырьков «Трафик × конверсия»):
 * страницы с базой — та же граница «мало данных», что у конверсии
 * страниц (`MARKETING_PAGES_MIN_TRAFFIC`), — и известной конверсией;
 * медиана их конверсии; ниже медианы — по `трафик × (медиана −
 * конверсия)` по убыванию. Строка «прочих страниц» — не страница сайта,
 * её не улучшить: в расчёт не входит.
 *
 * `denominator` — знаменатель окна из ответа: без него (посетителей не
 * считали) конверсий нет вовсе, и это не «мало данных», а «данных нет».
 */
export const marketingPageLosses = (
  rows: ReadonlyArray<IMarketingPageRow>,
  denominator: string | null | undefined,
): IMarketingPageLosses => {
  const based = rows
    .filter((row) => !marketingIsOtherPage(row.path))
    .map((row): Omit<IMarketingPageLoss, 'lost'> | null => {
      const { value } = marketingPageTraffic(row.metrics);
      const conversion = row.metrics.landingConversion ?? null;
      return value !== null &&
        value >= MARKETING_PAGES_MIN_TRAFFIC &&
        conversion !== null
        ? {
            path: row.path,
            pageType: row.pageType ?? null,
            traffic: value,
            conversion,
            leads: row.metrics.crmLeadsLanding,
          }
        : null;
    })
    .filter((row): row is Omit<IMarketingPageLoss, 'lost'> => row !== null);
  const empty = (
    state: MarketingPageLossState,
    med: number | null = null,
  ): IMarketingPageLosses => ({
    state,
    based: based.length,
    median: med,
    rows: [],
    lostTotal: 0,
  });
  if (!based.length && !isMarketingPageDenominator(denominator)) {
    return empty('noDenominator');
  }
  if (based.length < MARKETING_PAGES_LOSS_MIN_PAGES) return empty('smallBase');
  const med = median(based.map((row) => row.conversion)) as number;
  if (med <= 0) return empty('zeroMedian', med);
  const losing = based
    .filter((row) => row.conversion < med)
    .map((row) => ({
      ...row,
      lost: marketingPageLostLeads(row.traffic, row.conversion, med),
    }))
    .filter((row) => row.lost > 0)
    .sort((a, b) => b.lost - a.lost || a.path.localeCompare(b.path));
  return {
    state: 'ok',
    based: based.length,
    median: med,
    rows: losing,
    lostTotal: losing.reduce((sum, row) => sum + row.lost, 0),
  };
};

/**
 * «Недополучено ≈ N»: целое; меньше половины заявки — `null` («меньше 1»),
 * а не «≈ 0»: страница всё же ниже медианы.
 */
export const marketingPageLostRounded = (lost: number): number | null => {
  const rounded = Math.round(lost);
  return rounded >= 1 ? rounded : null;
};

/** Путей в выводе «Страниц»; остальные — «и ещё N». */
export const MARKETING_PAGES_INSIGHT_NAMED = 3;

/**
 * Вывод «Пути клиента › Страницы» одной фразой — из ТЕХ ЖЕ строк, что
 * список «Где теряем заявки» (`marketingPageLosses` ответа «по
 * посетителям»): сколько страниц с трафиком конвертируют хуже медианы,
 * какие и сколько заявок вместе недополучено. Нет знаменателя — «данных
 * нет», мало страниц — «данных мало», медиана 0 % — «сравнивать не с чем».
 */
export const marketingPagesInsight = (
  losses: IMarketingPageLosses | null | undefined,
  lang: string,
): IMarketingInsight | null => {
  if (!losses) return null;
  const count = (value: number) => formatMarketingCount(value, lang);
  if (losses.state === 'noDenominator') {
    return {
      tone: 'neutral',
      segments: [
        marketingInsightPart('marketing.insights.pages.noDenominator'),
        '.',
      ],
    };
  }
  if (losses.state === 'smallBase') {
    return {
      tone: 'neutral',
      segments: [
        marketingInsightPart('marketing.insights.pages.smallBase', {
          n: count(losses.based),
          min: count(MARKETING_PAGES_MIN_TRAFFIC),
          need: count(MARKETING_PAGES_LOSS_MIN_PAGES),
        }),
        '.',
      ],
    };
  }
  if (losses.state === 'zeroMedian') {
    return {
      tone: 'neutral',
      segments: [
        marketingInsightPart('marketing.insights.pages.zeroMedian', {
          n: count(losses.based),
        }),
        '.',
      ],
    };
  }
  if (!losses.rows.length) {
    return {
      tone: 'good',
      segments: [
        marketingInsightPart('marketing.insights.pages.none', {
          median: formatMarketingRate(losses.median, lang),
        }),
        '.',
      ],
    };
  }
  const named = losses.rows.slice(0, MARKETING_PAGES_INSIGHT_NAMED);
  const more = losses.rows.length - named.length;
  const list = named.map((row) => row.path).join(', ');
  const lost = marketingPageLostRounded(losses.lostTotal);
  return {
    tone: 'warn',
    segments: [
      more > 0
        ? marketingInsightPart('marketing.insights.pages.losingMore', {
            n: count(losses.rows.length),
            list,
            more: count(more),
          })
        : marketingInsightPart('marketing.insights.pages.losing', {
            n: count(losses.rows.length),
            list,
          }),
      '; ',
      lost === null
        ? marketingInsightPart('marketing.insights.pages.lostSmall')
        : marketingInsightPart('marketing.insights.pages.lost', {
            n: count(lost),
          }),
      '.',
    ],
  };
};

/**
 * Вывод над «Страницами» (финальная проверка 0103, F-E19): только когда
 * список «Где теряем заявки» посчитан. «Данных нет / мало / сравнивать не
 * с чем» говорит сама карточка прямо под выводом — та же фраза сверху была
 * повтором.
 */
export const marketingPagesHeadInsight = (
  losses: IMarketingPageLosses | null | undefined,
  lang: string,
): IMarketingInsight | null =>
  losses && losses.state === 'ok' ? marketingPagesInsight(losses, lang) : null;

/* ── «Посетители»: пустое окно (пакет 2) ─────────────────────────────── */

/**
 * В окне счётчика нет ничего — ни посетителей, ни визитов, ни лидов с
 * посетителем: вместо нулевых плиток и пустых осей — одна карточка.
 */
export const marketingVisitorsEmpty = (
  kpis: IMarketingVisitorKpis | null | undefined,
): boolean =>
  Boolean(kpis) &&
  (kpis as IMarketingVisitorKpis).visitors === 0 &&
  (kpis as IMarketingVisitorKpis).sessions === 0 &&
  (kpis as IMarketingVisitorKpis).linkedLeads === 0;

/* ── «Устройство заявки»: пока не показываем (пакет 2) ──────────────── */

/** Доля «неизвестно», с которой таблица устройств ещё ничего не говорит. */
export const MARKETING_DEVICE_UNKNOWN_HIDE = 0.9;

/**
 * Доля «неизвестно» в устройствах окна: по CRM-лидам (как столбец «Доля
 * лидов»), а если лидов нет — по продажам. Ни лидов, ни продаж — `null`
 * (у таблицы своё пустое состояние).
 */
export const marketingDeviceUnknownShare = (
  rows: ReadonlyArray<IMarketingDeviceRow>,
): number | null => {
  const sum = (
    pick: (row: IMarketingDeviceRow) => number,
    only?: (row: IMarketingDeviceRow) => boolean,
  ) =>
    rows
      .filter((row) => !only || only(row))
      .reduce((acc, row) => acc + pick(row), 0);
  const unknown = (row: IMarketingDeviceRow) =>
    marketingDeviceKey(row.device) === 'unknown';
  const leads = sum((row) => row.crmLeads);
  if (leads > 0) return sum((row) => row.crmLeads, unknown) / leads;
  const sales = sum((row) => row.sales);
  if (sales > 0) return sum((row) => row.sales, unknown) / sales;
  return null;
};

/** Прятать таблицу устройств: «неизвестно» — 90 % и больше. */
export const marketingDeviceHidden = (
  rows: ReadonlyArray<IMarketingDeviceRow>,
): boolean => {
  const share = marketingDeviceUnknownShare(rows);
  return share !== null && share >= MARKETING_DEVICE_UNKNOWN_HIDE;
};

/** «90 %» подписи — тем же форматом, что доли таблицы. */
export const marketingDeviceUnknownText = (
  rows: ReadonlyArray<IMarketingDeviceRow>,
  lang: string,
): string => formatMarketingPct(marketingDeviceUnknownShare(rows), lang);

/* ── «Качество данных»: связь посетителей и договоров ────────────────── */

export interface IMarketingCoverageShare {
  /** Сколько дошло до уровня (и глубже). */
  part: number;
  /** Всего (уровень `none` — все). */
  total: number;
  /** Доля; `null` — всего 0. */
  share: number | null;
}

/**
 * Доля договоров с известной кампанией из накопительного покрытия
 * (`none` — все договоры, `campaign` — с кампанией и глубже). Пусто —
 * `null`: «нет договоров» — не 0 %.
 */
export const marketingCoverageShareAt = (
  coverage: ReadonlyArray<IMarketingCoverageStepLike> | null | undefined,
  level: string,
): IMarketingCoverageShare | null => {
  if (!coverage || !coverage.length) return null;
  const total =
    coverage.find((step) => step.level === 'none')?.count ??
    Math.max(0, ...coverage.map((step) => step.count));
  const part = coverage.find((step) => step.level === level)?.count ?? 0;
  return { part, total, share: total > 0 ? part / total : null };
};

interface IMarketingCoverageStepLike {
  level: string;
  count: number;
}

/* ── «Настройки → Маркетинг»: правила типов страниц ──────────────────── */

/** Код типа страницы (как у сервера): латиница, цифры, `_`, до 16. */
export const MARKETING_PAGE_TYPE_CODE_RE = /^[a-z0-9_]{1,16}$/;

/** Правил типов страниц не больше (сервер, `MAX_PAGE_TYPE_RULES`). */
export const MARKETING_MAX_PAGE_TYPE_RULES = 50;

/** Длина regex правила (сервер, `MAX_PATTERN_LENGTH`). */
export const MARKETING_MAX_PAGE_TYPE_PATTERN = 200;

export type MarketingPageTypeRuleIssue =
  | 'typeInvalid'
  | 'typeReserved'
  | 'regexRequired'
  | 'regexTooLong'
  | 'regexInvalid';

/**
 * Проверка строки правила на экране — подсказка до сервера: код типа,
 * пустой / слишком длинный / не компилирующийся regex. `isValidPattern` —
 * JS-движок (окончательный отказ даёт Python сервера). `null` — всё хорошо.
 */
export const marketingPageTypeRuleIssue = (
  rule: { type: string; regex: string },
  isValidPattern: (pattern: string) => boolean,
): MarketingPageTypeRuleIssue | null => {
  const type = rule.type.trim().toLowerCase();
  const regex = rule.regex.trim();
  if (type === MARKETING_PAGE_TYPE_NONE) return 'typeReserved';
  if (!MARKETING_PAGE_TYPE_CODE_RE.test(type)) return 'typeInvalid';
  if (!regex) return 'regexRequired';
  if (regex.length > MARKETING_MAX_PAGE_TYPE_PATTERN) return 'regexTooLong';
  if (!isValidPattern(regex)) return 'regexInvalid';
  return null;
};

/** Строка правила в виде, в котором её сохраняет сервер. */
export const marketingNormalizePageTypeRule = (rule: {
  type: string;
  regex: string;
}): { type: string; regex: string } => ({
  type: rule.type.trim().toLowerCase(),
  regex: rule.regex.trim(),
});

/**
 * Повторы правил: для каждой строки — номер (с 1) первой строки выше с тем
 * же типом и regex (после нормализации), иначе `null`. Сервер повтор
 * принимает, но до него проверка не доходит: путь уже получил тип строки
 * выше. Строки без regex не сравниваются — у них своя ошибка.
 */
export const marketingPageTypeDuplicates = (
  rules: ReadonlyArray<{ type: string; regex: string }>,
): Array<number | null> => {
  const first = new Map<string, number>();
  return rules.map((rule, index) => {
    const { type, regex } = marketingNormalizePageTypeRule(rule);
    if (!regex) return null;
    const key = `${type}\u0000${regex}`;
    const seen = first.get(key);
    if (seen !== undefined) return seen + 1;
    first.set(key, index);
    return null;
  });
};

/**
 * Отказ сервера по строке: `rules.<i>.type|regex` → номер строки и поле;
 * общий отказ (`rules`) — `null`.
 */
export const marketingPageTypeErrorAt = (
  field: string,
): { index: number; field: 'type' | 'regex' } | null => {
  const match = /^rules\.(\d+)\.(type|regex)$/.exec(field);
  return match
    ? { index: Number(match[1]), field: match[2] as 'type' | 'regex' }
    : null;
};
